/**
 * A count of a reading against the true score, as the shares the Read board prints.
 *
 * The count itself is the pipeline's (`recognise/count.py`): notes found, notes right, the
 * piece's stretch of the reading. Here it is only divided, in one place, so the table and the
 * line under it cannot print two different numbers for one reading.
 */

import type { CountPayload, PageSpan } from "@app/shared-types";

/** Shares in [0, 1], or `null` where there was nothing to divide by. */
export interface Shares {
  /** Of the score's notes, those the reading has at their pitch and moment. */
  readonly found: number | null;
  /** Of the score's notes, those it has with their length too. */
  readonly withLength: number | null;
  /** Of the notes the reading strikes in the piece's stretch, those that are the score's. */
  readonly right: number | null;
  /** The harmonic mean of `found` and `right`: one number to tell two readers apart by. */
  readonly f1: number | null;
}

function share(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

export function sharesOf(count: CountPayload): Shares {
  const found = share(count.found, count.truthNotes);
  const right = share(count.right, count.readNotes);
  const f1 =
    found === null || right === null || found + right === 0
      ? null
      : (2 * found * right) / (found + right);
  return { found, withLength: share(count.withLength, count.truthNotes), right, f1 };
}

/** The notes read off one page's bars, and how many of them are the score's. */
export function onPage(count: CountPayload, span: PageSpan): { notes: number; right: number } {
  let notes = 0;
  let right = 0;
  for (const [struck, theirs] of count.bars.slice(span.first - 1, span.last)) {
    notes += struck;
    right += theirs;
  }
  return { notes, right };
}
